import React, { useState, useEffect } from "react";
import { Layout, Card, Flex, Typography } from "antd";
import { useSelector } from "react-redux";
import { getCookie } from "../../utils/cookieUtils";
import { getCompanySettings } from "../../services/organizationService";
import Sidebar from "../../general/components/left-menu/Sidebar";
import TopNavbar from "../../general/components/top-bar/TopNavbar";
import QuickOverview from "../../general/components/quick-overview/QuickOverview";
import AdminOverview from "./AdminOverview";
import LeadsTemplate from "../../general/components/leads-template/LeadsTemplate";
import OrganizationWorkspace from "../organization/OrganizationWorkspace";
import QuickAccessWorkspace from "../../general/components/quick-access/QuickAccessWorkspace";
import PipelineWorkspace from "../../general/components/pipeline/PipelineWorkspace";
import PeriodicReports from "../reports/PeriodicReports";
import EmployeesWorkspace from "../employees/EmployeesWorkspace";
import ActivitiesCalendar from "../../general/components/activities/ActivitiesCalendar";
import ClientOnboardRequestsSection from "../../general/components/startup_doctor/ClientOnboardRequestsSection";
import SubscriptionAlertBanner from "../../general/components/top-bar/SubscriptionAlertBanner";
import BottomNavigation from "../../general/components/bottom-navigation/BottomNavigation";

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const LEAD_WORKSPACE_TABS = new Set(["leads", "interested", "dnp", "converted", "favourite", "today-calls", "our_clients", "crm_clients", "clients", "remarks_filter"]);
const ORG_WORKSPACE_TABS = new Set(["company", "invoice_settings", "proposal_settings", "settings", "support"]);
const QUICK_ACCESS_TABS = new Set(["invoice", "quotation", "quotations", "proposals", "proposal"]);

const getTabTitle = (tab) => {
  const titles = {
    overview: "Dashboard",
    leads: "Leads",
    interested: "Hot / Qualified Leads",
    dnp: "Unreachable / Callbacks",
    converted: "Converted",
    favourite: "Priority Leads",
    "today-calls": "Today’s Call Log",
    our_clients: "Clients",
    crm_clients: "Enterprise Accounts",
    crm_sla: "Service Agreements (SLA)",
    crm_nda: "Legal & NDAs",
    crm_amc: "Maintenance Contracts (AMC)",
    crm_payments: "Milestone Payments",
    crm_tickets: "Support Helpdesk",
    crm_projects: "Client Deliverables",
    clients: "Clients",
    remarks_filter: "Notes Search",
    pipeline: "Pipeline",
    activities: "Schedule",
    crm_client_onboard_requests: "Onboarding",
    employees: "Employee Management",
    reports: "Reports",
    invoice: "Invoices",
    quotation: "Quotations",
    quotations: "Quotations",
    proposals: "Proposals",
    proposal: "Proposals",
    company: "Branding",
    invoice_settings: "Billing Settings",
    proposal_settings: "Proposal Templates",
    settings: "System Settings",
    support: "Help",
  };
  return titles[tab] || tab.replaceAll("_", " ");
};

const extractRecordSearchTerm = (tab, recordData) => {
  const isTargetTab = QUICK_ACCESS_TABS.has(tab) || tab === "pipeline";
  if (!isTargetTab || !recordData) {
    return { search: "", dealId: null };
  }
  const search =
    recordData?.data?.clientCompanyName ||
    recordData?.data?.leadCompanyName ||
    recordData?.data?.invoiceNumber ||
    recordData?.title ||
    "";
  const dealId = recordData?.data?._id || recordData?.data?.id || null;
  return { search, dealId };
};

const FallbackTabCard = ({ activeTab }) => (
  <Card bordered={false} style={{ borderRadius: 14, minHeight: 400 }}>
    <Flex vertical align="center" justify="center" style={{ padding: "4rem 0" }}>
      <Title level={3} style={{ margin: 0, textTransform: "capitalize" }}>
        {activeTab.replaceAll("_", " ")}
      </Title>
      <Paragraph type="secondary" style={{ marginTop: 8 }}>
        This section is ready for your {activeTab} data and components.
      </Paragraph>
    </Flex>
  </Card>
);

const AdminTabContent = ({
  activeTab,
  globalSearchTerm,
  quickAccessFilter,
  activitySubTab,
  setActivitySubTab,
  activityModalTrigger,
  pipelineFilters,
  setPipelineFilters,
  pipelineAddTrigger,
  onNavigateTab,
  onOpenForgotPwd,
  onReady,
}) => {
  if (activeTab === "overview") {
    return <AdminOverview onNavigateTab={onNavigateTab} onReady={onReady} />;
  }
  if (LEAD_WORKSPACE_TABS.has(activeTab)) {
    return <LeadsTemplate activeTab={activeTab} role="admin" searchQuery={globalSearchTerm} />;
  }
  if (ORG_WORKSPACE_TABS.has(activeTab)) {
    return <OrganizationWorkspace activeTab={activeTab} onOpenForgotPwd={onOpenForgotPwd} />;
  }
  if (QUICK_ACCESS_TABS.has(activeTab)) {
    return <QuickAccessWorkspace activeTab={activeTab} role="admin" initialSearch={quickAccessFilter.search} />;
  }
  if (activeTab === "pipeline") {
    return (
      <PipelineWorkspace
        role="admin"
        initialSearch={quickAccessFilter.search}
        targetDealId={quickAccessFilter.dealId}
        pipelineFilters={pipelineFilters}
        onPipelineFiltersChange={setPipelineFilters}
        addDealTrigger={pipelineAddTrigger}
        hideHeaderControls={true}
      />
    );
  }
  if (activeTab === "activities") {
    return (
      <ActivitiesCalendar
        role="admin"
        subTab={activitySubTab}
        onSubTabChange={setActivitySubTab}
        modalTrigger={activityModalTrigger}
        hideHeaderRail={true}
      />
    );
  }
  if (activeTab === "reports") {
    return <PeriodicReports />;
  }
  if (activeTab === "employees") {
    return <EmployeesWorkspace />;
  }
  if (activeTab === "crm_client_onboard_requests") {
    return <ClientOnboardRequestsSection />;
  }
  return <FallbackTabCard activeTab={activeTab} />;
};

function AdminDashboard({ onLogout, onOpenForgotPwd, onReady }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [isCrmAdmin, setIsCrmAdmin] = useState(true);
  const [activitySubTab, setActivitySubTab] = useState("calendar");
  const [activityModalTrigger, setActivityModalTrigger] = useState(0);
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [quickAccessFilter, setQuickAccessFilter] = useState({ search: "", dealId: null });
  const [pipelineFilters, setPipelineFilters] = useState({ search: "", owner: "all", priority: "all" });
  const [pipelineAddTrigger, setPipelineAddTrigger] = useState(0);

  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  useEffect(() => {
    if (!effectiveCompanyCode) return;
    getCompanySettings(effectiveCompanyCode)
      .then((res) => {
        if (res?.success && res.settings) {
          setIsCrmAdmin(res.settings.isCrmEnabled !== false);
        }
      })
      .catch((err) => console.warn("[AdminDashboard] Failed to check CRM access:", err));
  }, [effectiveCompanyCode]);

  const handleNavigateTab = (tab, recordData) => {
    setActiveTab(tab);
    setQuickAccessFilter(extractRecordSearchTerm(tab, recordData));
  };

  return (
    <Layout hasSider style={{ height: "100vh", overflow: "hidden" }}>
      <Sidebar role="admin" isCrmAdmin={isCrmAdmin} activeTab={activeTab} onTabChange={setActiveTab} />

      <Layout style={{ height: "100vh", overflow: "hidden", background: "#f8fafc", display: "flex", flexDirection: "column", transition: "all 0.2s ease" }}>
        <TopNavbar
          title={getTabTitle(activeTab)}
          activeTab={activeTab}
          onNavigateTab={handleNavigateTab}
          onLogout={onLogout}
          onSearch={setGlobalSearchTerm}
        />

        <SubscriptionAlertBanner
          companyCode={effectiveCompanyCode}
          onRenew={() => handleNavigateTab("company")}
        />

        <QuickOverview
          role="admin"
          activeTab={activeTab}
          onNavigateTab={setActiveTab}
          activitySubTab={activitySubTab}
          onActivitySubTabChange={setActivitySubTab}
          onCreateActivity={() => setActivityModalTrigger((prev) => prev + 1)}
          pipelineFilters={pipelineFilters}
          onPipelineFiltersChange={setPipelineFilters}
          onAddDeal={() => setPipelineAddTrigger((prev) => prev + 1)}
        />

        <Content
          className="hide-scrollbar"
          style={{
            padding: "1rem 1.25rem",
            flex: 1,
            overflowY: "auto",
          }}
        >
          <AdminTabContent
            activeTab={activeTab}
            globalSearchTerm={globalSearchTerm}
            quickAccessFilter={quickAccessFilter}
            activitySubTab={activitySubTab}
            setActivitySubTab={setActivitySubTab}
            activityModalTrigger={activityModalTrigger}
            pipelineFilters={pipelineFilters}
            setPipelineFilters={setPipelineFilters}
            pipelineAddTrigger={pipelineAddTrigger}
            onNavigateTab={setActiveTab}
            onOpenForgotPwd={onOpenForgotPwd}
            onReady={onReady}
          />
        </Content>

        <BottomNavigation
          role="admin"
          companyCode={effectiveCompanyCode}
          onNavigateTab={handleNavigateTab}
        />
      </Layout>
    </Layout>
  );
}

export default AdminDashboard;
